import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { fetchRooms, fetchMessages, fetchPinned, fetchConfig, fetchUsers, fetchGroups, fetchGroup, wsUrl } from "@/lib/chatApi";
import { playPing } from "@/lib/notify";

const TITLE = "RuangChat Live";

const mergeMessages = (prev, fetched) => {
  const ids = new Set(prev.map((m) => m.id));
  const missing = fetched.filter((m) => !ids.has(m.id));
  if (missing.length === 0) return prev;
  return [...prev, ...missing].sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0));
};

export function useChat(user, initialRoom = null, groupId = null, options = {}) {
  const [rooms, setRooms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [presence, setPresence] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [unread, setUnread] = useState({});
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("ruangchat_muted") === "1");
  const [moderators, setModerators] = useState([]);
  const [users, setUsers] = useState([]);
  const [lastByRoom, setLastByRoom] = useState(options.initialLast || {});

  const socketsRef = useRef({});
  const activeIdRef = useRef(null);
  const typingTimers = useRef({});
  const reconnectTimers = useRef({});
  const closingRef = useRef(false);
  const mutedRef = useRef(muted);
  const initialRoomRef = useRef(initialRoom || options.initialRoom || null);
  const groupIdRef = useRef(groupId);
  const roomIdsRef = useRef(options.roomIds || null);
  const desktopRef = useRef(!!options.desktopNotify);
  const roomsRef = useRef([]);

  useEffect(() => {
    mutedRef.current = muted;
    localStorage.setItem("ruangchat_muted", muted ? "1" : "0");
  }, [muted]);

  const handleEvent = useCallback(
    (roomId, data) => {
      const activeId = activeIdRef.current;
      if (data.type === "message") {
        const msg = data.message;
        setLastByRoom((prev) => ({ ...prev, [roomId]: msg }));
        if (
          desktopRef.current && msg.username !== user.username && "Notification" in window &&
          Notification.permission === "granted" && (roomId !== activeId || document.hidden)
        ) {
          const rn = roomsRef.current.find((r) => r.id === roomId)?.name || "Room";
          try {
            new Notification(`${msg.username} • ${rn}`, { body: msg.text || "Mengirim gambar", tag: roomId });
          } catch (e) {}
        }
        const mentionsMe =
          Array.isArray(msg.mentions) && msg.mentions.includes(user.username) && msg.username !== user.username;
        if (mentionsMe) {
          toast(`${msg.username} menyebut Anda`, { description: (msg.text || "").slice(0, 90) });
          if (!mutedRef.current) playPing();
        }
        if (roomId === activeId) {
          const isOther = msg.username !== user.username;
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
          if (isOther && !mentionsMe && !mutedRef.current) playPing();
        } else if (msg.username !== user.username) {
          setUnread((prev) => ({ ...prev, [roomId]: (prev[roomId] || 0) + 1 }));
          if (!mutedRef.current && !mentionsMe) playPing();
        }
      } else if (data.type === "presence") {
        if (roomId === activeId) setPresence(data.users);
      } else if (data.type === "typing") {
        if (roomId !== activeId || data.username === user.username) return;
        setTypingUsers((prev) => {
          if (data.is_typing) {
            clearTimeout(typingTimers.current[data.username]);
            typingTimers.current[data.username] = setTimeout(() => {
              setTypingUsers((p) => p.filter((u) => u !== data.username));
            }, 3000);
            return prev.includes(data.username) ? prev : [...prev, data.username];
          }
          return prev.filter((u) => u !== data.username);
        });
      } else if (data.type === "reaction") {
        if (roomId === activeId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === data.message_id ? { ...m, reactions: data.reactions } : m))
          );
        }
      } else if (data.type === "pin") {
        if (roomId !== activeId) return;
        setMessages((prev) =>
          prev.map((m) => (m.id === data.message_id ? { ...m, pinned: data.pinned } : m))
        );
        setPinned((prev) => {
          if (data.pinned) {
            if (prev.some((p) => p.id === data.message_id)) return prev;
            return [data.message, ...prev].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
          }
          return prev.filter((p) => p.id !== data.message_id);
        });
      } else if (data.type === "error") {
        toast.error(data.message || "Terjadi kesalahan");
      }
    },
    [user.username]
  );

  const connectRoom = useCallback(
    (roomId) => {
      if (socketsRef.current[roomId]) return;
      const active = roomId === activeIdRef.current;
      const ws = new WebSocket(wsUrl(roomId, user.username, user.avatar, active, user.token || null));
      socketsRef.current[roomId] = ws;

      ws.onopen = () => {
        const isActive = roomId === activeIdRef.current;
        ws.send(JSON.stringify({ type: "set_active", active: isActive }));
        if (isActive) {
          setConnected(true);
          fetchMessages(roomId).then((m) => {
            if (roomId === activeIdRef.current) setMessages(m);
          }).catch(() => {});
        }
      };
      ws.onmessage = (evt) => handleEvent(roomId, JSON.parse(evt.data));
      ws.onclose = () => {
        delete socketsRef.current[roomId];
        if (roomId === activeIdRef.current) setConnected(false);
        if (closingRef.current) return;
        reconnectTimers.current[roomId] = setTimeout(() => connectRoom(roomId), 2000);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {}
      };
    },
    [user.username, user.avatar, handleEvent]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let r = null;
      while (!cancelled && r === null) {
        try {
          r = await fetchRooms();
        } catch (e) {
          await new Promise((res) => setTimeout(res, 2000));
        }
      }
      if (cancelled) return;
      if (roomIdsRef.current) {
        const allow = new Set(roomIdsRef.current);
        r = r.filter((x) => allow.has(x.id));
      }
      const gid = groupIdRef.current;
      if (gid) {
        try {
          const g = await fetchGroup(gid);
          if (g && Array.isArray(g.room_ids)) {
            r = g.room_ids.map((id) => r.find((x) => x.id === id)).filter(Boolean);
          }
        } catch (e) {}
        if (cancelled) return;
      }
      setRooms(r);
      if (r.length) {
        const pref = initialRoomRef.current;
        let target = r[0];
        if (pref) {
          const p = String(pref).toLowerCase();
          target = r.find((x) => x.id === pref || (x.slug || "").toLowerCase() === p || x.name.toLowerCase() === p) || r[0];
        }
        activeIdRef.current = target.id;
        setActiveRoomId(target.id);
      }
    })();
    fetchConfig().then((c) => !cancelled && setModerators(c.moderators || [])).catch(() => {});
    fetchUsers().then((u) => !cancelled && setUsers(u)).catch(() => {});
    fetchGroups().then((g) => !cancelled && setGroups(g)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    roomsRef.current = rooms;
    rooms.forEach((r) => connectRoom(r.id));
  }, [rooms, connectRoom]);

  // Reliability: heartbeat keepalive + resync on focus/visibility + periodic
  // merge-refetch so messages never get stuck requiring a manual reload.
  useEffect(() => {
    if (!activeRoomId) return;

    const resync = () => {
      roomsRef.current.forEach((r) => {
        if (!socketsRef.current[r.id]) connectRoom(r.id);
      });
      fetchMessages(activeIdRef.current).then((m) => {
        setMessages((prev) => mergeMessages(prev, m));
      }).catch(() => {});
    };

    const refetchTimer = setInterval(resync, 12000);
    const heartbeat = setInterval(() => {
      Object.values(socketsRef.current).forEach((ws) => {
        try {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        } catch (e) {}
      });
    }, 25000);

    const onVisible = () => {
      if (document.visibilityState === "visible") resync();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      clearInterval(refetchTimer);
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [activeRoomId, connectRoom]);

  useEffect(() => {
    closingRef.current = false;
    return () => {
      closingRef.current = true;
      Object.values(reconnectTimers.current).forEach(clearTimeout);
      Object.values(socketsRef.current).forEach((ws) => {
        try {
          ws.close();
        } catch (e) {}
      });
      socketsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!activeRoomId) return;
    let cancelled = false;
    setMessages([]);
    setPinned([]);
    setTypingUsers([]);
    setPresence([]);
    setUnread((prev) => ({ ...prev, [activeRoomId]: 0 }));
    fetchMessages(activeRoomId).then((m) => {
      if (!cancelled) setMessages(m);
    }).catch(() => {});
    fetchPinned(activeRoomId).then((p) => {
      if (!cancelled) setPinned(p);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeRoomId]);

  const activeRoom = rooms.find((r) => r.id === activeRoomId) || null;
  const totalUnread = Object.entries(unread).reduce(
    (s, [rid, c]) => (rid !== activeRoomId ? s + c : s),
    0
  );
  const canModerate =
    !!activeRoom &&
    (moderators.includes(user.username) ||
      activeRoom.created_by === user.username ||
      (user.isAdmin && activeRoom.admin_username === user.username));

  useEffect(() => {
    let timer;
    const stop = () => {
      clearInterval(timer);
      document.title = TITLE;
    };
    const update = () => {
      stop();
      if (totalUnread > 0 && document.visibilityState === "hidden") {
        let on = false;
        timer = setInterval(() => {
          document.title = on ? TITLE : `(${totalUnread}) 💬 Pesan baru!`;
          on = !on;
        }, 1000);
      }
    };
    update();
    document.addEventListener("visibilitychange", update);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", update);
    };
  }, [totalUnread]);

  const selectRoom = useCallback((room) => {
    const prevId = activeIdRef.current;
    if (prevId === room.id) return;
    const prevWs = socketsRef.current[prevId];
    if (prevWs && prevWs.readyState === WebSocket.OPEN)
      prevWs.send(JSON.stringify({ type: "set_active", active: false }));
    activeIdRef.current = room.id;
    setActiveRoomId(room.id);
    const newWs = socketsRef.current[room.id];
    if (newWs && newWs.readyState === WebSocket.OPEN) {
      newWs.send(JSON.stringify({ type: "set_active", active: true }));
      setConnected(true);
    } else {
      setConnected(false);
      connectRoom(room.id);
    }
  }, [connectRoom]);

  const send = useCallback((payload) => {
    const ws = socketsRef.current[activeIdRef.current];
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
  }, []);

  const sendMessage = useCallback(
    (text, imageUrl, replyTo) => send({ type: "message", text, image_url: imageUrl, reply_to: replyTo || null }),
    [send]
  );
  const sendTyping = useCallback((isTyping) => send({ type: "typing", is_typing: isTyping }), [send]);
  const sendReaction = useCallback((messageId, emoji) => send({ type: "reaction", message_id: messageId, emoji }), [send]);
  const sendPin = useCallback((messageId, isPinned) => send({ type: "pin", message_id: messageId, pinned: isPinned }), [send]);
  const addRoom = useCallback((room) => setRooms((prev) => [...prev, room]), []);
  const updateRoom = useCallback(
    (room) => setRooms((prev) => prev.map((r) => (r.id === room.id ? { ...r, ...room } : r))),
    []
  );
  const refreshGroups = useCallback(() => {
    fetchGroups().then(setGroups).catch(() => {});
  }, []);

  return {
    rooms,
    groups,
    activeRoom,
    messages,
    pinned,
    presence,
    typingUsers,
    unread,
    totalUnread,
    connected,
    muted,
    setMuted,
    moderators,
    users,
    lastByRoom,
    canModerate,
    selectRoom,
    sendMessage,
    sendTyping,
    sendReaction,
    sendPin,
    addRoom,
    updateRoom,
    refreshGroups,
  };
}
